// apps/web/src/components/perfis/quem-tem-o-que.tsx — quick 261006-lf9.
// "Quem tem o quê": pessoas com as permissões efetivas e catálogo de permissões
// com os perfis que as concedem. Conteúdo puro (props); ids só como chave interna.
import { useState, type ReactElement } from 'react';
import { agruparPorModulo } from '../../lib/permissoes-catalogo';
import { rotuloStatus } from '../../lib/rotulos';
import type { PerfilResumo, PessoaDoPerfil } from './comum';

const BOTAO =
  'min-h-[44px] rounded border border-border px-4 text-sm font-semibold text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

export interface PessoasPermissoesConteudoProps {
  readonly usuarios: readonly PessoaDoPerfil[];
  /** Pessoa com as permissões abertas (acordeão controlado pelo wrapper). */
  readonly expandidoId?: string | null;
  readonly onAlternar?: (usuarioId: string) => void;
  readonly onTrocarPerfil?: (pessoa: PessoaDoPerfil) => void;
}

export function PessoasPermissoesConteudo({ usuarios, expandidoId = null, onAlternar, onTrocarPerfil }: PessoasPermissoesConteudoProps): ReactElement {
  if (usuarios.length === 0) {
    return <p className="text-sm text-muted-foreground">Nenhuma pessoa cadastrada.</p>;
  }
  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0" aria-label="Pessoas e permissões">
      {usuarios.map((pessoa) => {
        const aberto = pessoa.id === expandidoId;
        const grupos = aberto ? agruparPorModulo(pessoa.permissions) : [];
        return (
          <li key={pessoa.id} className="rounded-lg border border-border bg-card p-4 text-card-foreground">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex min-w-0 flex-col">
                <span className="break-words font-semibold">{pessoa.displayName || pessoa.username}</span>
                <span className="break-words text-sm text-muted-foreground">
                  {pessoa.username} · Perfil: {pessoa.role.name} · {rotuloStatus(pessoa.status)}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" aria-expanded={aberto} onClick={() => onAlternar?.(pessoa.id)} className={BOTAO}>
                  {aberto ? 'Ocultar permissões' : 'Ver permissões'}
                </button>
                {onTrocarPerfil ? (
                  <button type="button" onClick={() => onTrocarPerfil(pessoa)} className={BOTAO}>Trocar perfil</button>
                ) : null}
              </div>
            </div>
            {aberto ? (
              <div className="mt-4 flex flex-col gap-4" data-testid="pessoa-permissoes">
                {grupos.length === 0 ? <p className="text-sm text-muted-foreground">Este perfil não concede nenhuma permissão.</p> : null}
                {grupos.map((grupo) => (
                  <div key={grupo.modulo}>
                    <h4 className="text-sm font-semibold text-foreground">{grupo.rotulo}</h4>
                    <ul className="m-0 list-none p-0 text-sm text-muted-foreground">
                      {grupo.itens.map((item) => <li key={item.chave}>{item.rotuloAcao}: {item.descricao}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/** Wrapper com estado: uma pessoa aberta por vez. */
export function PessoasPermissoes(props: Omit<PessoasPermissoesConteudoProps, 'expandidoId' | 'onAlternar'>): ReactElement {
  const [expandidoId, setExpandidoId] = useState<string | null>(null);
  return (
    <PessoasPermissoesConteudo
      {...props}
      expandidoId={expandidoId}
      onAlternar={(id) => setExpandidoId((atual) => (atual === id ? null : id))}
    />
  );
}

export interface CatalogoPermissoesConteudoProps {
  readonly catalogo: readonly string[];
  readonly perfis: readonly PerfilResumo[];
}

export function CatalogoPermissoesConteudo({ catalogo, perfis }: CatalogoPermissoesConteudoProps): ReactElement {
  if (catalogo.length === 0) {
    return <p className="text-sm text-muted-foreground">O catálogo de permissões não está disponível agora.</p>;
  }
  return (
    <div className="flex flex-col gap-6">
      {agruparPorModulo(catalogo).map((grupo) => (
        <section key={grupo.modulo} aria-label={grupo.rotulo} className="rounded-lg border border-border bg-card p-4 text-card-foreground">
          <h3 className="mb-2 text-lg font-semibold text-foreground">{grupo.rotulo}</h3>
          <ul className="m-0 flex list-none flex-col gap-4 p-0">
            {grupo.itens.map((item) => {
              const quem = perfis.filter((perfil) => perfil.permissoes.includes(item.chave)).map((perfil) => perfil.nome);
              return (
                <li key={item.chave} className="flex flex-col gap-1">
                  <span className="font-semibold">{item.rotuloAcao}</span>
                  <span className="break-words text-sm text-muted-foreground">{item.descricao}</span>
                  <span className="break-words text-xs text-muted-foreground">
                    Perfis com esta permissão: {quem.length > 0 ? quem.join(', ') : 'Nenhum perfil'}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
